/**
 * push-presence-cellular — 클라이언트 검증 presence. iOS PresenceClient.swift / AOS PresenceClient.kt 와 동일 계약.
 *
 * 배경: 서버는 "수신자가 오프라인일 때만" 채팅 푸시를 보낸다. 종전엔 그 판정을 SSE 소켓 생존으로 했는데,
 *   소켓 생존을 갱신하는 주체가 **서버 자신의 30s ping** 이었다 → half-open 소켓(모바일 셀룰러에서 FIN 이
 *   서버에 도달 못 하는 경우)을 살아있다고 오판해 푸시가 통째로 억제됐다.
 *
 * → presence 를 **클라이언트가 증명**하는 값으로 바꿨다. 서버는 이 하트비트만으로 presence 를 유지한다(TTL 60s).
 *
 * ⚠ 웹이 이 하트비트를 보내야 하는 이유(회귀 방지): 웹은 TopBar 에서 로그인 세션 내내 `/sse/me` 를 유지한다.
 *   웹이 presence 를 증명하지 않으면 서버가 사용자를 "오프라인" 으로 보고, **웹으로 일하는 중에 폰으로 푸시가
 *   쏟아진다**. 종전엔 웹의 SSE 소켓 자체가 presence 였기에 이 문제가 없었다.
 *
 * 전 요청 best-effort — 실패해도 throw 하지 않는다(1회 실패로 하트비트 루프가 죽으면 presence 가 만료된다).
 */

/** 하트비트 주기. 서버 TTL(60s)의 절반 이하 — 1회 누락까지 견딘다. */
const HEARTBEAT_MS = 25_000;

async function post(baseUrl: string, path: string, clientId: string): Promise<void> {
  try {
    await fetch(`${baseUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include', // auth v1.5 — cookie wc_at 자동 동봉 (SSE 와 동일)
      body: JSON.stringify({ clientId }),
      keepalive: true, // 탭 종료/이탈 중에도 전송 시도
    });
  } catch {
    /* best-effort — 다음 주기가 복구 */
  }
}

export interface PresenceHandle {
  /** 하트비트 중단 + presence 즉시 반납. */
  stop: () => void;
}

/**
 * SSE 연결 수명 동안 presence 를 유지한다. 반환된 stop() 을 SSE close 시 호출할 것.
 *
 * stop() 이 offline 을 즉시 POST 하는 이유: TTL(60s)을 기다리지 않고 곧바로 푸시로 전환시키기 위함.
 * 탭을 그냥 닫는 경우엔 pagehide 에서 sendBeacon 으로 보낸다(fetch 는 언로드 중 취소될 수 있음).
 */
export function startPresence(baseUrl: string, clientId: string): PresenceHandle {
  let stopped = false;

  const timer = window.setInterval(() => {
    if (stopped) return;
    void post(baseUrl, '/chat/presence/heartbeat', clientId);
  }, HEARTBEAT_MS);

  // 탭 종료 — unload 중 fetch 는 취소될 수 있으므로 sendBeacon(브라우저가 배송 보장) 사용.
  const onPageHide = () => {
    const url = `${baseUrl}/chat/presence/offline`;
    const body = new Blob([JSON.stringify({ clientId })], { type: 'application/json' });
    if (!navigator.sendBeacon?.(url, body)) void post(baseUrl, '/chat/presence/offline', clientId);
  };
  window.addEventListener('pagehide', onPageHide);

  return {
    stop: () => {
      if (stopped) return;
      stopped = true;
      window.clearInterval(timer);
      window.removeEventListener('pagehide', onPageHide);
      void post(baseUrl, '/chat/presence/offline', clientId);
    },
  };
}

/** 연결마다 새로 만든다 — 서버 connId 는 클라이언트에 노출되지 않으므로 클라이언트가 식별자를 생성한다. */
export function newClientId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
